import { Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';
import { interpretarComoUtc } from '../utils/data-local.util';

/**
 * Exibe em hora local um campo cuja coluna do banco é gravada **em UTC**.
 *
 * ## Quando usar — e quando NÃO usar
 *
 * Este pipe é **opt-in por campo**, e aplicá-lo no campo errado transforma dado
 * certo em errado na tela. Antes de usar, consulte o mapa de colunas da change
 * `padronizar-fuso-horario-datas`:
 *
 * - coluna **UTC pura** (todos os escritores gravam UTC) → usar este pipe;
 * - coluna **local pura** → usar o `| date` normal, que já acerta;
 * - coluna **mista** (escritores em UTC e em local) → usar o `| date` normal.
 *   Não há correção possível: a coluna tem linhas certas e linhas +3h, sem nada
 *   na linha que diga qual é qual. Compensar corrigiria umas e estragaria outras.
 *
 * Por isso este pipe **não** substitui o `DatePipe` globalmente. O `DatePipe`
 * não tem defeito de fuso — diante de string sem offset ele exibe o valor
 * literal da coluna, que é exatamente o certo para coluna local. Sobrescrevê-lo
 * estragaria em silêncio as exibições que hoje estão corretas.
 *
 * ## Como compensa
 *
 * Lendo o texto como relógio **UTC** e deixando a plataforma converter para o
 * fuso do navegador:
 *
 *     "2026-08-17T13:30:00"     →  exibe 10:30
 *     "08/17/2026 13:30:00"     →  exibe 10:30   (formato invariante do Dapper)
 *
 * E não subtraindo 3h fixas: uma constante embute o offset no código e passa a
 * errar se o horário de verão voltar.
 *
 * O segundo formato existe porque vários responses tipam a data como `string`
 * em vez de `DateTime`, e aí o Dapper serializa com cultura invariante em vez de
 * ISO. Ver o aviso em `interpretarComoUtc` — é a parte frágil disto.
 */
@Pipe({ name: 'dataUtc' })
export class DataUtcPipe implements PipeTransform {

  constructor(private datePipe: DatePipe) { }

  transform(valor: string | Date | null | undefined, formato = 'dd/MM/yyyy HH:mm'): string {
    if (valor === null || valor === undefined || valor === '') return '';

    // `Date` já representa um instante — não há string sem fuso para
    // reinterpretar, e anexar `Z` aqui não significaria nada. Só formata.
    if (valor instanceof Date) {
      return this.datePipe.transform(valor, formato) ?? '';
    }

    const texto = valor.trim();
    if (!texto) return '';

    // interpretarComoUtc devolve o instante quando reconhece um formato que o
    // servidor manda sem fuso, e null quando não deve compensar (string com `Z`,
    // data pura, formato desconhecido). Nesse caso o texto segue intacto para o
    // DatePipe, que é o comportamento de antes deste pipe existir.
    const instante = interpretarComoUtc(texto);

    return this.datePipe.transform(instante ?? texto, formato) ?? '';
  }
}
